/**
 * TradingView Webhook Service
 * Handles communication with the TradingView webhook API
 *
 * NOTE: TradingView webhook endpoints are not yet implemented in the Strategy Engine.
 * This service returns mock data until the backend endpoints are available.
 */

import { API } from "./API";

// Check if we're in production (Cloudflare Pages) or development
const isProduction =
  window.location.hostname !== "localhost" &&
  window.location.hostname !== "127.0.0.1";

// Mock data for production until TradingView endpoints are implemented
const MOCK_DATA = {
  status: {
    status: "pending_setup",
    total_signals_received: 0,
    total_trades_executed: 0,
    last_signal: null,
    last_trade: null,
    message: "TradingView webhook integration coming soon",
  },
  signals: { total: 0, signals: [] },
  trades: { total: 0, trades: [] },
  config: {
    enabled: false,
    secret_key: "",
    allowed_symbols: [],
    max_daily_trades: 50,
    require_confirmation: false,
    auto_execute: false,
    risk_management: {
      max_risk_per_trade: 2.0,
      max_daily_loss: 5.0,
      max_position_size_percent: 10.0,
    },
  },
  brokers: { brokers: [], default_broker: null },
  brokerStatus: {
    broker: "paper_trading",
    connected: false,
    balance: { cash: 0, total_value: 0 },
    positions: [],
    daily_stats: { trades_count: 0, total_pnl: 0 },
  },
};

const TradingViewWebhookService = {
  /**
   * Get webhook status
   */
  getStatus: async () => {
    // In production, return mock data until endpoints are implemented
    if (isProduction) {
      return MOCK_DATA.status;
    }
    try {
      const response = await API.get("/tradingview/status");
      return response.data;
    } catch (error) {
      console.error("Error getting webhook status:", error);
      return MOCK_DATA.status;
    }
  },

  /**
   * Get received signals
   */
  getSignals: async (limit = 50, status = null) => {
    if (isProduction) {
      return MOCK_DATA.signals;
    }
    try {
      const params = { limit };
      if (status) params.status = status;
      const response = await API.get("/tradingview/signals", { params });
      return response.data;
    } catch (error) {
      console.error("Error getting signals:", error);
      return MOCK_DATA.signals;
    }
  },

  /**
   * Get executed trades
   */
  getTrades: async (limit = 50) => {
    if (isProduction) {
      return MOCK_DATA.trades;
    }
    try {
      const response = await API.get("/tradingview/trades", {
        params: { limit },
      });
      return response.data;
    } catch (error) {
      console.error("Error getting trades:", error);
      return MOCK_DATA.trades;
    }
  },

  /**
   * Get webhook configuration
   */
  getConfig: async (userId = "default") => {
    if (isProduction) {
      return MOCK_DATA.config;
    }
    try {
      const response = await API.get("/tradingview/config", {
        params: { user_id: userId },
      });
      return response.data;
    } catch (error) {
      console.error("Error getting webhook config:", error);
      return MOCK_DATA.config;
    }
  },

  /**
   * Update webhook configuration
   */
  updateConfig: async (config, userId = "default") => {
    if (isProduction) {
      console.warn("TradingView config update not available in production yet");
      return { success: false, message: "Feature coming soon" };
    }
    try {
      const response = await API.post("/tradingview/config", config, {
        params: { user_id: userId },
      });
      return response.data;
    } catch (error) {
      console.error("Error updating webhook config:", error);
      throw error;
    }
  },

  /**
   * Test webhook endpoint
   */
  testWebhook: async () => {
    if (isProduction) {
      return { success: false, message: "Feature coming soon" };
    }
    try {
      const response = await API.post("/tradingview/test");
      return response.data;
    } catch (error) {
      console.error("Error testing webhook:", error);
      throw error;
    }
  },

  /**
   * Connect to a broker
   */
  connectBroker: async (brokerId, credentials, setDefault = true) => {
    if (isProduction) {
      return { success: false, message: "Feature coming soon" };
    }
    try {
      const response = await API.post(
        "/tradingview/broker/connect",
        credentials,
        {
          params: { broker_id: brokerId, set_default: setDefault },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error connecting broker:", error);
      throw error;
    }
  },

  /**
   * Disconnect from a broker
   */
  disconnectBroker: async (brokerId) => {
    if (isProduction) {
      return { success: false, message: "Feature coming soon" };
    }
    try {
      const response = await API.delete(`/tradingview/broker/${brokerId}`);
      return response.data;
    } catch (error) {
      console.error("Error disconnecting broker:", error);
      throw error;
    }
  },

  /**
   * Get broker status
   */
  getBrokerStatus: async (brokerId = null) => {
    if (isProduction) {
      return MOCK_DATA.brokerStatus;
    }
    try {
      const params = brokerId ? { broker_id: brokerId } : {};
      const response = await API.get("/tradingview/broker/status", { params });
      return response.data;
    } catch (error) {
      console.error("Error getting broker status:", error);
      return MOCK_DATA.brokerStatus;
    }
  },

  /**
   * List all connected brokers
   */
  listBrokers: async () => {
    if (isProduction) {
      return MOCK_DATA.brokers;
    }
    try {
      const response = await API.get("/tradingview/broker/list");
      return response.data;
    } catch (error) {
      console.error("Error listing brokers:", error);
      return MOCK_DATA.brokers;
    }
  },

  /**
   * Generate webhook URL for TradingView
   */
  generateWebhookUrl: (baseUrl, secretKey) => {
    // The webhook URL that TradingView will POST to
    return `${baseUrl}/api/tradingview/webhook`;
  },

  /**
   * Generate sample TradingView alert message
   */
  generateSampleAlert: (action, symbol, options = {}) => {
    return JSON.stringify(
      {
        action: action, // "buy", "sell", "close"
        symbol: symbol,
        order_type: options.orderType || "market",
        quantity: options.quantity || null,
        price: options.price || null,
        stop_loss: options.stopLoss || null,
        take_profit: options.takeProfit || null,
        risk_percent: options.riskPercent || 2,
        leverage: options.leverage || 1,
        strategy_name: options.strategyName || "TradingView Strategy",
        passphrase: options.passphrase || "your_webhook_secret",
        comment: options.comment || "",
      },
      null,
      2,
    );
  },
};

export default TradingViewWebhookService;
